import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Save, Phone, Sparkles, Mail, X, Copy, Zap, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AcceptItineraryModal from "./AcceptItineraryModal";
import LiveOptionsPanel from "./LiveOptionsPanel";

export default function BudgetTable({ budget, sashaReply, onBook, onMembership }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [bookingModal, setBookingModal] = useState(null); // {mailto, tel, recall_code, ...}
  const [submittingBooking, setSubmittingBooking] = useState(false);
  const [savedItin, setSavedItin] = useState(null);
  const [acceptOpen, setAcceptOpen] = useState(false);
  if (!budget || !budget.items?.length) return null;

  const saveItinerary = async () => {
    if (!user) {
      toast.message("Sign in to save your itinerary.");
      navigate("/login?next=/chat");
      return;
    }
    setSaving(true);
    try {
      const today = new Date();
      const start = today.toISOString().slice(0, 10);
      const nights = parseInt(budget.nights || "5", 10) || 5;
      const end = new Date(today.getTime() + nights * 86400000).toISOString().slice(0, 10);
      const dest = budget.destination || "Custom Destination";
      const items = budget.items.map((it) => {
        const cat = (it.category || "").toLowerCase();
        let type = "activity";
        let provider = "preferred_activities";
        if (cat.includes("flight")) { type = "flight"; provider = "preferred_flights"; }
        else if (cat.includes("car")) { type = "car"; provider = "preferred_cars"; }
        else if (cat.includes("hotel") || cat.includes("stay")) { type = "hotel"; provider = "host"; }
        return {
          type,
          title: it.description || it.category,
          description: `${it.category} · $${it.price}`,
          provider,
          price: it.price,
        };
      });
      const r = await api.post("/itineraries", {
        title: budget.title || `Trip to ${dest}`,
        destination: dest,
        start_date: start,
        end_date: end,
        travelers: parseInt(budget.travelers || "2", 10) || 2,
        notes: `Estimated total: $${budget.total.toLocaleString()}`,
        items,
      });
      setSavedItin(r.data);
      toast.success(`Saved · Recall code ${r.data.recall_code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const requestBooking = async () => {
    setSubmittingBooking(true);
    try {
      // Save first if needed so we have a recall_code linked to the itinerary
      let itinId = savedItin?.id;
      if (!itinId) {
        await saveItinerary();
        itinId = savedItin?.id;
      }
      const r = await api.post("/booking-requests", {
        itinerary_id: itinId,
        name: user?.name || "Guest",
        email: user?.email || "guest@jante.travel",
        notes: budget.title,
      });
      setBookingModal(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not file booking request.");
    } finally {
      setSubmittingBooking(false);
    }
  };

  return (
    <div
      className="lux-card mt-3 max-w-3xl border-t-4 border-[var(--gold)] no-copy"
      data-testid="budget-table"
      data-protected="true"
      onCopy={(e) => {
        e.preventDefault();
        toast.message("Copying is disabled while booking APIs are being finalised.");
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="overline">Budgeting itinerary</div>
          <div className="font-display text-3xl text-[var(--emerald)] mt-1">
            {budget.title || "Trip Budget"}
          </div>
          {(budget.destination || budget.nights || budget.travelers) && (
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ink-2)] mt-1.5">
              {budget.destination}
              {budget.nights && ` · ${budget.nights} nights`}
              {budget.travelers && ` · ${budget.travelers} travelers`}
            </div>
          )}
          {savedItin && (
            <div className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[var(--brand-red)]">
              <span className="w-2 h-px bg-[var(--brand-red)]" /> Recall code: {savedItin.recall_code}
            </div>
          )}
          {budget.live_pricing && (
            <div
              className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[var(--emerald)] bg-[var(--emerald)]/8 border border-[var(--emerald)]/25 px-2.5 py-1"
              data-testid="live-pricing-badge"
              title="Pricing pulled live from Janté preferred partner inventory"
            >
              <Zap size={11} strokeWidth={2} className="text-[var(--gold)]" />
              Live partner pricing
            </div>
          )}
        </div>
        <Sparkles size={18} strokeWidth={1.25} className="text-[var(--gold)]" />
      </div>

      {/* Two-column itemized list */}
      <div className="border-t border-[var(--emerald)]/15">
        {budget.items
          .filter((it) => it.pricing_source !== "amadeus_consolidated")
          .map((it, i) => (
            <div
              key={`${it.category}-${it.description}-${i}`}
              className="grid grid-cols-[1fr_auto] gap-4 py-3 border-b border-[var(--emerald)]/10 items-start"
              data-testid={`budget-row-${i}`}
            >
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[var(--brand-red)]">
                  {it.category}
                  {it.pricing_source === "amadeus" && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 text-[9px] tracking-[0.14em] text-[var(--emerald)] normal-case"
                      data-testid={`budget-row-live-${i}`}
                      title="Live partner pricing"
                    >
                      <Zap size={9} strokeWidth={2} /> live
                    </span>
                  )}
                </div>
                <div className="text-sm text-[var(--ink)] mt-0.5 leading-snug">{it.description}</div>
              </div>
              <div className="font-display text-2xl text-[var(--emerald)] tabular-nums">
                ${it.price.toLocaleString()}
              </div>
            </div>
          ))}

        <div
          className="grid grid-cols-[1fr_auto] gap-4 py-4 mt-1 bg-[var(--ivory-2)] -mx-8 px-8 items-baseline"
          data-testid="budget-total"
        >
          <div className="overline">Estimated total</div>
          <div className="font-display text-4xl text-[var(--emerald)] tabular-nums">
            ${budget.total.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Action row */}
      <div className="flex flex-wrap gap-3 mt-6">
        <button
          onClick={() => setAcceptOpen(true)}
          className="btn-primary !bg-[var(--brand-red)] hover:!bg-[var(--brand-red)]/90"
          data-testid="budget-accept-pay-btn"
        >
          <Lock size={14} strokeWidth={1.5} /> Place reservation · $
          {Math.min(2000, Math.max(100, Math.round((Number(budget.total) || 0) * 0.1))).toLocaleString()}
        </button>
        <button onClick={saveItinerary} disabled={saving} className="btn-primary" data-testid="budget-save-btn">
          <Save size={14} strokeWidth={1.5} /> {saving ? "Saving…" : savedItin ? "Saved" : "Save itinerary"}
        </button>
        <button
          onClick={onMembership}
          className="text-xs uppercase tracking-[0.18em] text-[var(--brand-red)] hover:text-[var(--emerald)] px-3 py-2 self-center"
          data-testid="budget-membership-btn"
        >
          See subscriptions
        </button>
      </div>

      <AcceptItineraryModal
        open={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        budget={budget}
        sashaReply={sashaReply}
        tripRef={savedItin?.recall_code || null}
        itineraryId={savedItin?.id || null}
        userEmail={user?.email}
      />

      {/* Live Amadeus flight & hotel options, attached to the budget by the
          backend. Renders nothing when no options are available. */}
      <LiveOptionsPanel budget={budget} />

      {/* Booking modal */}
      {bookingModal && (
        <div className="fixed inset-0 bg-[var(--emerald)]/40 backdrop-blur-sm z-50 flex items-center justify-center px-4" data-testid="booking-modal">
          <div className="bg-[var(--ivory)] max-w-lg w-full p-8 relative">
            <button
              onClick={() => setBookingModal(null)}
              className="absolute top-4 right-4 text-[var(--muted)] hover:text-[var(--emerald)]"
              data-testid="booking-modal-close"
            >
              <X size={18} />
            </button>
            <div className="overline">Booking filed</div>
            <h3 className="font-display text-4xl text-[var(--emerald)] mt-2">A concierge has it.</h3>
            <p className="text-sm text-[var(--ink-2)] mt-3">
              Your booking request has been logged with our team. Reach them now or share your recall code.
            </p>
            <div className="mt-5 lux-card-dark !p-5 flex items-center justify-between gap-3">
              <div>
                <div className="overline text-[var(--gold)]">Trip recall code</div>
                <div className="font-display text-3xl mt-1">{bookingModal.recall_code}</div>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(bookingModal.recall_code);
                  toast.success("Copied.");
                }}
                className="btn-gold !py-2 !px-3 text-[10px]"
                data-testid="booking-copy-recall"
              >
                <Copy size={12} strokeWidth={1.5} /> Copy
              </button>
            </div>
            <div className="mt-6 flex flex-col gap-3">
              <a
                href={bookingModal.tel}
                className="btn-primary w-full"
                data-testid="booking-call-btn"
              >
                <Phone size={14} strokeWidth={1.5} /> Call concierge — {bookingModal.concierge_phone}
              </a>
              <a
                href={bookingModal.mailto}
                className="btn-gold w-full"
                data-testid="booking-email-btn"
              >
                <Mail size={14} strokeWidth={1.25} /> Email booking form to {bookingModal.concierge_email}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
